import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { NetworkRequest } from "../../../../../../common/models";
import { OrganizationDataRecipientDto } from "../../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { VendorOutcomeOption } from "../../../../../../common/service/server/Types";
import { useAuthorizations } from "../../../../../hooks/useAuthorizations";
import { usePrimaryOrganization } from "../../../../../hooks/useOrganization";
import { useOrganizationVendors } from "../../../../../hooks/useOrganizationVendors";
import { Routes } from "../../../../../root/routes";
import { sortVendorsByCategory } from "../../../../../util/vendorCategorization";
import { Abbr } from "../../../../Abbr";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import { useSurvey } from "../../../../../../common/hooks/api/useSurvey";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../../../../pages/get-compliant/data-recipients/shared";
import {
  getRequestProcessingRegion,
  isRequestContextApplicable,
} from "../../../../../util/vendors";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";
import { useManagedAdNetworks } from "../../../../../../common/hooks/dataRecipientSettingsHooks";
import { parseMultiselectAnswerWithNoneOption } from "../../../../../surveys/survey";
import { RetainedVendorRow } from "../shared/RetainedVendorRow";
import { VendorDeleteRow } from "../shared/VendorDeleteRow";
import { CategorizedVendorList } from "../shared/CategorizedVendorList";
import { Callout, CalloutVariant } from "../../../../Callout";
import { alphabetically } from "../../../../../surveys/util";

export type VendorOutcomeHandler = (
  vendor: OrganizationDataRecipientDto,
  outcome: VendorOutcomeOption,
) => void;

export type VendorOutcomeCollection = Record<string, string>;

export const RestrictProcessingStep = () => {
  const [org, orgRequest] = usePrimaryOrganization();
  const [allVendors, vendorsRequest] = useOrganizationVendors(org.id, undefined, true);
  const { request, setStepCompleted, mutations } = useDataRequestProcessingContext();
  const [readyToRender, setReadyToRender] = useState(false);
  const { data: dataMap } = useDataMap(org.id);

  const { answers } = useSurvey(org.id, SELLING_AND_SHARING_SURVEY_NAME);
  const { managedAdNetworks } = useManagedAdNetworks(org.id);

  const { authorizations, request: authorizationsRequest } = useAuthorizations(org.id);

  const onSelectionChange: VendorOutcomeHandler = (vendor, value) => {
    mutations.updateVendorOutcomeMutation.mutate({ vendor: vendor.vendorId, outcome: value });
  };

  // Effects
  useEffect(() => {
    if (NetworkRequest.areFinished(vendorsRequest, orgRequest, authorizationsRequest)) {
      setReadyToRender(true);
    }
  }, [orgRequest, vendorsRequest, authorizationsRequest]);

  const requestProcessingRegion = getRequestProcessingRegion(request);

  // Get the selected custom audience vendor managers from the survey
  const customAudienceVendorManagers = parseMultiselectAnswerWithNoneOption(
    answers,
    "custom-audience-feature-separate-vendor",
  );

  // Get all network IDs that are managed by someone (only if the manager is still selected)
  const managedAdNetworksIds: string[] = [
    ...new Set(
      managedAdNetworks
        ?.filter((item) => customAudienceVendorManagers.includes(item.managerId))
        ?.map((item) => item.networkId) || [],
    ),
  ];

  // Get all unique manager IDs
  const managementVendorIds: string[] = [
    ...new Set(managedAdNetworks?.map((item) => item.managerId) || []),
  ];

  // Filter management vendors to only include those that are still selected in the survey
  const managementVendors =
    allVendors?.filter(
      (v) => managementVendorIds.includes(v.id) && customAudienceVendorManagers.includes(v.id),
    ) || [];

  const vendors = useMemo(() => {
    if (!allVendors) return undefined;

    let result = allVendors?.filter((v) => {
      const matchesProcessingRegion = v.processingRegions.includes(requestProcessingRegion);
      const instruction = v.instructions.find((vi) => vi.requestType == "DELETE");
      const instructionOk = instruction?.processingMethod != "INACCESSIBLE_OR_NOT_STORED";
      const contextOk = isRequestContextApplicable(dataMap, request.context, v);

      // If a management vendor exists, then hide vendors that are managed by the management vendor
      if (managementVendors.length > 0 && managedAdNetworksIds.includes(v.id)) {
        return false;
      }

      return matchesProcessingRegion && instructionOk && contextOk;
    });

    // Add management vendors that should appear in restriction workflows (similar to opt-out workflow)
    if (managementVendors.length > 0 && managedAdNetworksIds.length > 0) {
      const managementVendorsToAdd = managementVendors.filter((vendor) => {
        // Skip if already in vendors list
        if (result?.find((v) => v.id === vendor.id)) {
          return false;
        }

        // Use simpler criteria like opt-out workflow - just check that there are managed networks
        return managedAdNetworksIds.length > 0;
      });

      if (managementVendorsToAdd.length > 0) {
        result?.push(...managementVendorsToAdd);
      }
    }

    result?.sort(alphabetically);
    return result;
  }, [
    allVendors,
    requestProcessingRegion,
    dataMap,
    request,
    answers,
    managementVendors,
    managedAdNetworksIds,
  ]);

  const installedVendors = useMemo(() => {
    return sortVendorsByCategory(vendors?.filter((v) => !v.isInstalled) ?? []);
  }, [vendors]);

  const deleteVendors = useMemo(() => {
    return installedVendors.filter(
      (v) =>
        !v.instructions.some(
          (i) =>
            i.requestType == "DELETE" &&
            i.processingMethod === "RETAIN" &&
            !i.hasRetentionExceptions,
        ),
    );
  }, [installedVendors]);

  const retainVendors = useMemo(() => {
    return installedVendors.filter((v) =>
      v.instructions.some((i) => i.requestType == "DELETE" && i.processingMethod === "RETAIN"),
    );
  }, [installedVendors]);

  useEffect(() => {
    const outcomes = request.vendorOutcomes;
    const everyOutcomeCompleted = deleteVendors.every((vendor) => {
      const outcome = outcomes?.find((o) => o.vendor.id == vendor.vendorId);
      return (
        (outcome?.outcome != null && outcome?.outcome !== "AUTOMATION_FAILED") ||
        vendor.isInstalled === true
      );
    });

    setStepCompleted(
      Boolean(deleteVendors) && deleteVendors.length <= outcomes.length && everyOutcomeCompleted,
    );
  }, [setStepCompleted, deleteVendors, request.vendorOutcomes, vendors]);

  const vendorOutcomes = useMemo(() => {
    const outcomes = request.vendorOutcomes.reduce(
      (acc, outcome) => Object.assign({}, acc, { [outcome.vendor.id]: outcome.outcome }),
      {},
    );

    request._local?.pendingVendorOutcomeUpdates?.forEach((pending) => {
      outcomes[pending.vendorId] = pending.outcome;
    });

    return outcomes;
  }, [request.vendorOutcomes, request._local?.pendingVendorOutcomeUpdates]);

  const pendingUpdates = useMemo(() => {
    const pending = request._local?.pendingVendorOutcomeUpdates?.map((pending) => pending.vendorId);
    return new Set(pending);
  }, [request._local?.pendingVendorOutcomeUpdates]);

  if (!readyToRender) return null;

  return (
    <>
      <div className="text-t3 mb-std">
        To fulfill a Request to Restrict Processing, you are required to stop all processing of the
        consumer's personal data except to store it. This restriction remains in place as long as
        the consumer has a legal basis for the request. For example, if the consumer submitted a
        Request to Restrict Processing while a Request to Correct is being reviewed, the restriction
        will end once the Request to Correct has been completed.{" "}
        <a
          className="text-weight-regular"
          href="https://knowledge.truevault.com/article/317-request-to-restrict-processing"
          target="_blank"
          rel="noopener noreferrer"
        >
          Read more about Requests to Restrict Processing.
        </a>
      </div>

      <Callout
        variant={CalloutVariant.Clear}
        title={`The consumer provided the following details in their ${request.dataRequestType.label}`}
        className="mb-md"
      >
        {request.description}
      </Callout>

      {deleteVendors && (
        <CategorizedVendorList
          vendors={deleteVendors}
          renderVendor={(vendor) => {
            // Check if this vendor is a management vendor
            const isManagementVendor = managementVendors.some((v) => v.id === vendor.id);
            const customAudienceManagementVendor = isManagementVendor ? vendor : undefined;

            return (
              <VendorDeleteRow
                key={vendor.id}
                vendor={vendor}
                authorizations={authorizations}
                vendorOutcomes={vendorOutcomes}
                updatePending={pendingUpdates.has(vendor.vendorId)}
                onSelectionChange={onSelectionChange}
                org={org}
                request={request}
                managedCustomAudienceVendors={
                  customAudienceManagementVendor
                    ? managedAdNetworksIds
                        .map((id) => allVendors.find((v) => v.id === id))
                        .filter(Boolean)
                    : undefined
                }
                customAudienceManagementVendor={customAudienceManagementVendor}
              />
            );
          }}
        />
      )}
      {retainVendors && retainVendors.length > 0 && (
        <>
          <div className="text-t5 text-weight-medium mb-md"> Data Retention </div>

          <div className="text-t3 mb-mdlg">
            The systems below are marked for retention based on one or more{" "}
            <Abbr title="Your business may retain personal data under certain exceptions, such as for internal use and to comply with a legal obligation.">
              <strong>exceptions to deletion</strong>
            </Abbr>
            . Do not delete data stored with these Data Recipients if it is kept under an exception
            to deletion. Data retention settings can be updated in your{" "}
            <Link to={`${Routes.dataMap.root}?tab=data_recipients`}>Data Map</Link>.
          </div>

          <CategorizedVendorList
            vendors={retainVendors}
            renderVendor={(vendor) => <RetainedVendorRow key={vendor.id} vendor={vendor} />}
          />
        </>
      )}
    </>
  );
};
